import type { KeyNameType } from '@tunasong/music-lib'
import {
  alternateSpelling,
  displayRoot,
  getKeyNames,
  isValidSpelling,
  keyDisplayName,
  respellForType,
  rootSpellingsFor,
  samePitch,
  spokenKeyName,
  spokenRootName,
} from '@tunasong/music-lib'
import type { NoteName, Scale, ScaleType } from '@tunasong/schemas'
import {
  Menu,
  MenuContent,
  MenuGroup,
  MenuGroupLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, KeyboardEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import styles from './key-select.module.css'

export interface KeySelectProps {
  className?: string
  value?: Partial<Scale> | null
  /** The root grid's accessible name. @default "Root" */
  title?: string
  variant?: KeyNameType
  allowNone?: boolean
  /** `menu` on desktop and tablet; `view` opens "More scales" in place, for the phone drawer (spec §2.5) */
  moreAs?: 'menu' | 'view'
  /** The phone view's back button, named for where it returns. @default "Back" */
  moreBackLabel?: string
  onChange(scale: Scale | null): void
}

/** Spec §2.5: modes in the order they fall on the major scale, then the other scales */
const modes: ScaleType[] = ['dorian', 'phrygian', 'lydian', 'mixolydian', 'locrian']
const otherScales: ScaleType[] = ['harmonicminor', 'melodicminor', 'majorpentatonic', 'minorpentatonic']

const scaleLabel: Partial<Record<ScaleType, string>> = {
  dorian: 'Dorian',
  phrygian: 'Phrygian',
  lydian: 'Lydian',
  mixolydian: 'Mixolydian',
  locrian: 'Locrian',
  harmonicminor: 'Harmonic minor',
  melodicminor: 'Melodic minor',
  majorpentatonic: 'Major pentatonic',
  minorpentatonic: 'Minor pentatonic',
}

const pretty = (note: string) => note.replace(/#/g, '♯').replace(/b/g, '♭')

const isMajor = (type?: ScaleType) => !type || type === 'major' || type === 'ionian'
const isMinor = (type?: ScaleType) => type === 'minor' || type === 'aeolian'

/**
 * One key control (key selector K-S2, #2764; spec docs/ux/2026-10-24-key-selector.md §2, §3, §5): the read-back
 * with Clear key, the 12 roots spelled for the scale and selected by pitch, "Spell as", and Major | Minor | More….
 * It stores what it shows, re-spells only when a type change needs it, and never rewrites a stored legacy value.
 */
export const KeySelect: FC<KeySelectProps> = props => {
  const {
    className,
    value = null,
    title,
    variant = 'common',
    allowNone = true,
    moreAs = 'menu',
    moreBackLabel = 'Back',
    onChange,
  } = props

  const stored = value?.root ? { root: value.root, type: value.type } : null
  /** With no key, a type choice relabels the grid and waits for a root */
  const [unsetType, setUnsetType] = useState<ScaleType>('major')
  const type: ScaleType = stored?.type ?? (stored ? 'major' : unsetType)
  const grid = rootSpellingsFor(type)
  /** The selected cell keeps the stored spelling when it is valid for the type (a user's "Spell as") */
  const labels = grid.map(note =>
    stored && samePitch(note, stored.root) && isValidSpelling(stored.root, type) ? stored.root : note
  )
  const selected = stored ? labels.findIndex(note => samePitch(note, stored.root)) : -1
  const [focusIndex, setFocusIndex] = useState(Math.max(0, selected))
  const tabStop = selected >= 0 ? selected : focusIndex
  const cellRefs = useRef<(HTMLButtonElement | null)[]>([])
  const moreRef = useRef<HTMLButtonElement | null>(null)
  const headingRef = useRef<HTMLHeadingElement | null>(null)
  const [view, setView] = useState<'main' | 'more'>('main')
  const [returnFocus, setReturnFocus] = useState(false)

  const keyNames = getKeyNames(variant)
  const moreModes = modes.filter(name => keyNames.includes(name))
  const moreOthers = otherScales.filter(name => keyNames.includes(name))
  const hasMore = variant !== 'simple' && moreModes.length + moreOthers.length > 0
  const moreSelected = stored && !isMajor(stored.type) && !isMinor(stored.type) ? stored.type : undefined

  useEffect(() => {
    if (view === 'more') {
      headingRef.current?.focus()
    }
    if (view === 'main' && returnFocus) {
      moreRef.current?.focus()
      setReturnFocus(false)
    }
  }, [view, returnFocus])

  const pickRoot = (index: number) => {
    const root = labels[index]
    if (!root) {
      return
    }
    setFocusIndex(index)
    onChange({ root, type })
  }

  const pickType = (next: ScaleType) => {
    if (!stored) {
      setUnsetType(next)
      return
    }
    onChange({ root: respellForType(stored.root, next), type: next })
  }

  /** APG radio group: arrows move and select; Up/Down move a row of six; Home/End go to C and B */
  const handleGridKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const from = tabStop
    const moves: Record<string, number> = {
      ArrowRight: (from + 1) % 12,
      ArrowLeft: (from + 11) % 12,
      ArrowDown: (from + 6) % 12,
      ArrowUp: (from + 6) % 12,
      Home: 0,
      End: 11,
    }
    const to = moves[event.key]
    if (to === undefined) {
      return
    }
    event.preventDefault()
    cellRefs.current[to]?.focus()
    pickRoot(to)
  }

  const alternate = stored ? alternateSpelling(displayRoot({ root: stored.root, type }), type) : null

  if (view === 'more') {
    return (
      <div className={cn(styles.keySelect, className)}>
        <div className={styles.moreHeader}>
          <button
            type="button"
            className={styles.textButton}
            onClick={() => {
              setView('main')
              setReturnFocus(true)
            }}
          >
            {moreBackLabel}
          </button>
          <h2 ref={headingRef} tabIndex={-1} className={styles.moreTitle}>
            More scales
          </h2>
        </div>
        <div role="radiogroup" aria-label="More scales" className={styles.moreList}>
          {[
            { label: 'Modes', names: moreModes },
            { label: 'Other scales', names: moreOthers },
          ].map(group =>
            group.names.length > 0 ? (
              <div key={group.label} role="group" aria-label={group.label}>
                <h3 className={styles.groupLabel} aria-hidden="true">
                  {group.label}
                </h3>
                {group.names.map(name => (
                  <button
                    key={name}
                    type="button"
                    role="radio"
                    aria-checked={stored?.type === name}
                    className={styles.moreOption}
                    onClick={() => {
                      pickType(name)
                      setView('main')
                    }}
                  >
                    {scaleLabel[name]}
                  </button>
                ))}
              </div>
            ) : null
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={cn(styles.keySelect, className)}>
      <div className={styles.readout}>
        <span
          className={cn(styles.name, !stored && styles.unset)}
          aria-label={stored ? spokenKeyName({ root: stored.root, type }) : undefined}
        >
          {stored ? keyDisplayName({ root: stored.root, type }) : 'No key'}
        </span>
        {stored && allowNone ? (
          <button type="button" className={styles.textButton} onClick={() => onChange(null)}>
            Clear key
          </button>
        ) : null}
      </div>

      <div role="radiogroup" aria-label={title ?? 'Root'} className={styles.grid} onKeyDown={handleGridKey}>
        {labels.map((note, index) => (
          <button
            key={index}
            ref={element => {
              cellRefs.current[index] = element
            }}
            type="button"
            role="radio"
            aria-checked={index === selected}
            aria-label={spokenRootName(note)}
            tabIndex={index === tabStop ? 0 : -1}
            className={styles.cell}
            onClick={() => pickRoot(index)}
          >
            {pretty(note)}
          </button>
        ))}
      </div>

      {alternate && stored ? (
        <button
          type="button"
          className={cn(styles.textButton, styles.spell)}
          onClick={() => onChange({ root: alternate as NoteName, type })}
        >
          {`Spell as ${pretty(alternate)}`}
        </button>
      ) : null}

      <div className={styles.scaleRow}>
        <div role="radiogroup" aria-label="Scale" className={styles.segments}>
          <button
            type="button"
            role="radio"
            aria-checked={!moreSelected && isMajor(type)}
            className={styles.segment}
            onClick={() => pickType('major')}
          >
            Major
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={!moreSelected && isMinor(type)}
            className={styles.segment}
            onClick={() => pickType('minor')}
          >
            Minor
          </button>
        </div>
        {hasMore && moreAs === 'view' ? (
          <button
            ref={moreRef}
            type="button"
            className={cn(styles.segment, styles.more)}
            aria-pressed={Boolean(moreSelected)}
            aria-label={moreSelected ? `Scale: ${scaleLabel[moreSelected]}, more scales` : undefined}
            onClick={() => setView('more')}
          >
            {moreSelected ? scaleLabel[moreSelected] : 'More…'}
          </button>
        ) : null}
        {hasMore && moreAs === 'menu' ? (
          <Menu>
            <MenuTrigger
              render={
                <button
                  ref={moreRef}
                  type="button"
                  className={cn(styles.segment, styles.more)}
                  data-selected={Boolean(moreSelected)}
                  aria-label={moreSelected ? `Scale: ${scaleLabel[moreSelected]}, more scales` : undefined}
                >
                  {moreSelected ? `${scaleLabel[moreSelected]} ⌄` : 'More…'}
                </button>
              }
            />
            <MenuContent>
              <MenuRadioGroup value={moreSelected ?? ''} onValueChange={next => pickType(next as ScaleType)}>
                {moreModes.length > 0 ? (
                  <MenuGroup>
                    <MenuGroupLabel>Modes</MenuGroupLabel>
                    {moreModes.map(name => (
                      <MenuRadioItem key={name} value={name}>
                        {scaleLabel[name]}
                      </MenuRadioItem>
                    ))}
                  </MenuGroup>
                ) : null}
                {moreModes.length > 0 && moreOthers.length > 0 ? <MenuSeparator /> : null}
                {moreOthers.length > 0 ? (
                  <MenuGroup>
                    <MenuGroupLabel>Other scales</MenuGroupLabel>
                    {moreOthers.map(name => (
                      <MenuRadioItem key={name} value={name}>
                        {scaleLabel[name]}
                      </MenuRadioItem>
                    ))}
                  </MenuGroup>
                ) : null}
              </MenuRadioGroup>
            </MenuContent>
          </Menu>
        ) : null}
      </div>
    </div>
  )
}
